import { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import ChartCard from "../ChartCard";
import {
  CHART_TITLE,
  ENERGY_BAR_COLOR,
  SESSIONS_BAR_COLOR,
  GRID_STROKE,
  BAR_RADIUS,
  BAR_SIZE,
  CHART_HEIGHT,
} from "./constants";

function EnergySessionsTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const energyEntry = payload.find(p => p.dataKey === "energy");
  const sessionsEntry = payload.find(p => p.dataKey === "sessions");
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: "10px 14px",
        fontSize: 12,
        boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
        minWidth: 160,
      }}
    >
      <div
        style={{
          fontWeight: 600,
          marginBottom: 8,
          color: "#374151",
          borderBottom: "1px solid #f3f4f6",
          paddingBottom: 6,
        }}
      >
        {label}
      </div>
      {energyEntry && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <span
            style={{ width: 10, height: 10, borderRadius: 2, background: ENERGY_BAR_COLOR, display: "inline-block" }}
          />
          <span style={{ color: "#6b7280" }}>Energy:</span>
          <span style={{ fontWeight: 700, color: "#111827", marginLeft: "auto" }}>
            {energyEntry.value.toLocaleString()} kWh
          </span>
        </div>
      )}
      {sessionsEntry && (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: SESSIONS_BAR_COLOR,
              display: "inline-block",
            }}
          />
          <span style={{ color: "#6b7280" }}>Sessions:</span>
          <span style={{ fontWeight: 700, color: "#111827", marginLeft: "auto" }}>
            {sessionsEntry.value.toLocaleString()}
          </span>
        </div>
      )}
    </div>
  );
}

EnergySessionsTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.string,
};

export default function EnergySessionsChart({ data, loading }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const [isTableExpanded, setIsTableExpanded] = useState(false);

  const handleMouseMove = useCallback(state => {
    if (state?.activeTooltipIndex != null) {
      setActiveIndex(state.activeTooltipIndex);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setActiveIndex(null);
  }, []);

  const toggleTable = useCallback(() => {
    setIsTableExpanded(prev => !prev);
  }, []);

  const cursorStyle = { fill: "rgba(0,0,0,0.04)" };

  // Calculate totals
  const totalSessions = data.reduce((sum, d) => sum + d.sessions, 0);
  const totalEnergy = data.reduce((sum, d) => sum + d.energy, 0);

  if (loading) {
    return (
      <ChartCard title={CHART_TITLE} badge="">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: CHART_HEIGHT,
            color: "#9ca3af",
            fontSize: 13,
            gap: 8,
          }}
        >
          <Loader2 size={20} className="kpi-card__spinner" />
          Loading chart data…
        </div>
      </ChartCard>
    );
  }

  return (
    <ChartCard title={CHART_TITLE} badge={`${data.length} Days`}>
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <ComposedChart data={data} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
          <defs>
            <linearGradient id="energyBarGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={ENERGY_BAR_COLOR} stopOpacity={0.9} />
              <stop offset="100%" stopColor={ENERGY_BAR_COLOR} stopOpacity={0.5} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 10, fill: "#9ca3af" }}
            axisLine={{ stroke: "#e5e7eb" }}
            tickLine={false}
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis
            yAxisId="energy"
            orientation="left"
            tick={{ fontSize: 11, fill: "#9ca3af" }}
            axisLine={false}
            tickLine={false}
            width={50}
            label={{
              value: "Energy(kWh)",
              angle: -90,
              position: "insideLeft",
              offset: 10,
              style: { fontSize: 11, fill: "#9ca3af", fontWeight: 600 },
            }}
          />
          <YAxis
            yAxisId="sessions"
            orientation="right"
            tick={{ fontSize: 11, fill: "#9ca3af" }}
            axisLine={false}
            tickLine={false}
            width={50}
            label={{
              value: "Sessions",
              angle: 90,
              position: "insideRight",
              offset: 10,
              style: { fontSize: 11, fill: "#9ca3af", fontWeight: 600 },
            }}
          />
          <Tooltip
            content={<EnergySessionsTooltip />}
            cursor={cursorStyle}
            active={activeIndex != null}
            defaultIndex={activeIndex}
          />
          <Legend
            wrapperStyle={{ paddingTop: "20px" }}
            formatter={value => {
              if (value === "energy" || value === "Energy (kWh)") return "Energy (kWh)";
              if (value === "sessions" || value === "Sessions") return "Sessions";
              return value;
            }}
          />
          <Bar
            yAxisId="energy"
            dataKey="energy"
            name="Energy (kWh)"
            fill="url(#energyBarGrad)"
            barSize={BAR_SIZE}
            radius={BAR_RADIUS}
          />
          <Line
            yAxisId="sessions"
            type="monotone"
            dataKey="sessions"
            name="Sessions"
            stroke={SESSIONS_BAR_COLOR}
            strokeWidth={2.5}
            dot={{ r: 4, fill: SESSIONS_BAR_COLOR, stroke: "#fff", strokeWidth: 2 }}
            activeDot={{ r: 6, fill: SESSIONS_BAR_COLOR, stroke: "#fff", strokeWidth: 2 }}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Expand/Collapse button for table */}
      <div style={{ marginTop: "16px" }}>
        <button
          type="button"
          onClick={toggleTable}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 0px",
            background: "transparent",
            border: "none",
            borderRadius: "0",
            color: "#2563eb",
            fontSize: "12px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s ease",
            width: "100%",
            justifyContent: "flex-start",
          }}
          onMouseEnter={e => {
            e.target.style.color = "#1d4ed8";
            e.target.style.textDecoration = "underline";
          }}
          onMouseLeave={e => {
            e.target.style.color = "#2563eb";
            e.target.style.textDecoration = "none";
          }}
        >
          {isTableExpanded ? (
            <>
              <ChevronUp size={14} />
              Hide Data Table
            </>
          ) : (
            <>
              <ChevronDown size={14} />
              Show Data Table ({data.length} days)
            </>
          )}
        </button>

        {/* Collapsible table content */}
        <div
          style={{
            overflow: "hidden",
            transition: "max-height 0.3s ease-in-out, opacity 0.3s ease-in-out",
            maxHeight: isTableExpanded ? "1000px" : "0",
            opacity: isTableExpanded ? 1 : 0,
            marginTop: isTableExpanded ? "12px" : "0",
          }}
        >
          <div className="chart-data-table" style={{ maxHeight: "none", overflow: "visible" }}>
            <table className="chart-data-table__table">
              <thead>
                <tr>
                  <th className="chart-data-table__th">Date</th>
                  <th className="chart-data-table__th chart-data-table__th--center">Sessions</th>
                  <th className="chart-data-table__th chart-data-table__th--right">Energy (kWh)</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, idx) => (
                  <tr key={idx} className="chart-data-table__row">
                    <td className="chart-data-table__td">{item.date}</td>
                    <td className="chart-data-table__td chart-data-table__td--center">
                      {item.sessions.toLocaleString()}
                    </td>
                    <td className="chart-data-table__td chart-data-table__td--right">{item.energy.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="chart-data-table__total">
                  <td className="chart-data-table__td">Total</td>
                  <td className="chart-data-table__td chart-data-table__td--center">
                    {totalSessions.toLocaleString()}
                  </td>
                  <td className="chart-data-table__td chart-data-table__td--right">{totalEnergy.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </ChartCard>
  );
}

EnergySessionsChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      energy: PropTypes.number.isRequired,
      sessions: PropTypes.number.isRequired,
    })
  ).isRequired,
  loading: PropTypes.bool,
};

EnergySessionsChart.defaultProps = {
  loading: false,
};
